import { useState } from "react";
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  Mail,
  Users,
} from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "./ui/field";
import { Input } from "./ui/input";

const LOGO_LETTERS = [
  ["B", "#7A3CBD"],
  ["I", "#F43F9A"],
  ["R", "#2F6BFF"],
  ["C", "#22C55E"],
  ["H", "#F5B942"],
  ["W", "#FF8A1F"],
  ["O", "#7A3CBD"],
  ["O", "#F4D03F"],
  ["D", "#E53935"],
];

const HIGHLIGHTS = [
  {
    title: "Students",
    copy: "Admissions & records",
    icon: Users,
    tint: "bg-[#7A3CBD]",
  },
  {
    title: "Academic",
    copy: "Schedules & attendance",
    icon: CalendarDays,
    tint: "bg-[#22A06B]",
  },
  {
    title: "Operations",
    copy: "Reports & administration",
    icon: BarChart3,
    tint: "bg-[#E08A2C]",
  },
];

function BirchwoodMark() {
  return (
    <div
      className="flex items-center justify-center"
      aria-label="Birchwood"
      style={{
        fontFamily: '"Nunito", system-ui, sans-serif',
        fontWeight: 900,
        fontSize: "1.55rem",
        lineHeight: 1,
        letterSpacing: "-0.045em",
      }}
    >
      {LOGO_LETTERS.map(([letter, color], index) => (
        <span key={`${letter}${index}`} style={{ color }}>
          {letter}
        </span>
      ))}
    </div>
  );
}

function fieldIcon(field) {
  if (field.type === "email" || field.name === "email") return Mail;
  if (field.type === "password") return Lock;
  if (field.name?.toLowerCase().includes("code")) return KeyRound;
  return Mail;
}

function AuthInput({ type, className, icon: Icon, ...props }) {
  const [visible, setVisible] = useState(false);
  const isPassword = type === "password";

  return (
    <div className="relative">
      {Icon ? (
        <Icon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
      ) : null}
      <Input
        type={isPassword && visible ? "text" : type}
        className={cn(
          "h-9 rounded-md border-slate-200 bg-[#F3F6FA] px-3 text-[13px] text-slate-700 shadow-none",
          "placeholder:text-slate-400",
          "focus-visible:border-[#1A3B70]/30 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#1A3B70]/15",
          Icon && "pl-9",
          isPassword && "pr-9",
          className
        )}
        {...props}
      />
      {isPassword ? (
        <button
          type="button"
          className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition-colors hover:text-slate-600"
          onClick={() => setVisible((value) => !value)}
          aria-label={visible ? "Hide password" : "Show password"}
        >
          {visible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
        </button>
      ) : null}
    </div>
  );
}

export function LoginForm({
  className,
  title = "Welcome back",
  description = "Log in to your Birchwood admin account.",
  submitLabel = "Log in",
  loading = false,
  fields = [],
  extra,
  footer,
  onSubmit,
  ...props
}) {
  const handleSubmit = (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const values = Object.fromEntries(formData.entries());
    onSubmit?.(values);
  };

  return (
    <div className={cn("w-full", className)} {...props}>
      <div className="overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_rgba(26,59,112,0.14)]">
        <div className="grid md:grid-cols-2">
          <form
            className="flex flex-col justify-center px-8 py-7 md:px-9 md:py-7"
            onSubmit={handleSubmit}
          >
            <FieldGroup className="gap-5">
              <div className="flex flex-col items-center text-center">
                <BirchwoodMark />
                <p className="mt-1.5 text-[10px] font-semibold tracking-[0.32em] text-[#1A3B70]">
                  MONTESSORI ACADEMY
                </p>
                <div className="mt-5 flex w-full items-center gap-3">
                  <span className="h-px flex-1 bg-slate-200" />
                  <span className="text-[10px] font-medium tracking-[0.28em] text-slate-400">
                    ADMIN PORTAL
                  </span>
                  <span className="h-px flex-1 bg-slate-200" />
                </div>
              </div>

              <div>
                <h1 className="text-[1.35rem] font-bold tracking-tight text-[#1A3B70]">
                  {title}
                </h1>
                <p className="mt-1 text-[13px] text-slate-500">{description}</p>
              </div>

              {fields.map((field) => (
                <Field key={field.name} className="gap-1.5">
                  <div className="flex items-center justify-between gap-3">
                    <FieldLabel
                      htmlFor={field.name}
                      className="text-xs font-medium text-slate-500"
                    >
                      {field.label}
                    </FieldLabel>
                    {field.action}
                  </div>
                  <AuthInput
                    id={field.name}
                    name={field.name}
                    type={field.type || "text"}
                    icon={fieldIcon(field)}
                    placeholder={field.placeholder}
                    required={field.required !== false}
                    minLength={field.minLength}
                    autoComplete={field.autoComplete}
                    defaultValue={field.defaultValue}
                    spellCheck={false}
                  />
                </Field>
              ))}

              {extra}

              <Field>
                <Button
                  type="submit"
                  disabled={loading}
                  className="h-9 w-full rounded-md bg-[#1A3B70] text-[13px] font-semibold text-white hover:bg-[#16325f]"
                >
                  {loading ? <Loader2 className="animate-spin" /> : null}
                  {loading ? "Please wait..." : submitLabel}
                  {loading ? null : <ArrowRight className="h-4 w-4" />}
                </Button>
              </Field>

              {footer ? (
                <FieldDescription className="text-center">{footer}</FieldDescription>
              ) : null}
            </FieldGroup>
          </form>

          <div className="relative hidden min-h-[500px] overflow-hidden md:block">
            <img
              src="/images/login_bg.png"
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#12325c]/90 via-[#12325c]/45 to-transparent" />
            <div className="relative z-10 flex h-full flex-col justify-center px-8 py-8 text-white">
              <p className="text-[10px] font-medium tracking-[0.34em] text-white/70">
                ADMIN PORTAL
              </p>
              <h2
                className="mt-3 text-[1.7rem] font-semibold leading-[1.15]"
                style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
              >
                Support Today
                <br />
                Brighter Tomorrows
              </h2>
              <span className="mt-4 h-0.5 w-12 rounded-full bg-[#E8A317]" />
              <p className="mt-3 text-[13px] text-white/85">
                Manage. Monitor. Make a difference.
              </p>
              <ul className="mt-6 space-y-3">
                {HIGHLIGHTS.map((item) => (
                  <li key={item.title} className="flex items-center gap-3">
                    <span
                      className={cn(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                        item.tint
                      )}
                    >
                      <item.icon className="h-3.5 w-3.5 text-white" />
                    </span>
                    <span>
                      <span className="block text-[13px] font-semibold">{item.title}</span>
                      <span className="block text-xs text-white/70">{item.copy}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <p className="mt-4 px-6 text-center text-[11px] leading-5 text-slate-400">
        By logging in, you agree to our{" "}
        <a href="#terms" className="text-[#2F6BFF] underline underline-offset-2">
          Terms of Service
        </a>{" "}
        and{" "}
        <a href="#privacy" className="text-[#2F6BFF] underline underline-offset-2">
          Privacy Policy
        </a>
        .
      </p>
    </div>
  );
}

export function AuthPage({ children }) {
  return (
    <div
      className="relative flex min-h-svh flex-col items-center justify-center p-8 font-sans md:p-12"
      style={{
        backgroundColor: "#EAF3FB",
        backgroundImage: "url('/images/loginBg.png')",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "cover",
      }}
    >
      <div className="relative z-10 w-full max-w-sm md:max-w-[800px]">{children}</div>
    </div>
  );
}
